import {
    QueryError,
    queryStateData,
} from "../../../../../utils/supabase/QueryState";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { constNull, flow, pipe } from "fp-ts/function";
import { liveResultsRoute } from "../../../../../routes/org";
import { array, either, nonEmptyArray, option, task, taskEither } from "fp-ts";
import {
    errorsToMessage,
    optionOrChain,
    runTask,
} from "../../../../../utils/fp";
import { executeSupabaseQuery } from "../../../../../utils/supabase/useQuery";
import {
    eventDetailCodec,
    eventDetailQuery,
    eventDetailQueryId,
    EventDetailRace,
    eventDetailRaceOrd,
} from "../../../../../hooks/event";
import { supabase } from "../../../../../utils/supabase/init";
import { reportError } from "../../../../../utils/reporting";
import {
    Autocomplete,
    Button,
    ButtonGroup,
    Container,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { EventTitle } from "../../../../../components/org/EventTitle";
import { messages } from "../../../../../utils/i18n/i18n";
import { validIsoDateStringFromDateCodec } from "../../../../../domain/common";
import * as React from "react";
import { EventId, RaceId } from "../../../../../domain/event";
import { addMinutes, isWithinInterval } from "date-fns";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import {
    RaceTabs,
    useRaceTabState,
} from "../../../../../components/org/event/RaceTabs";
import { useTranslation } from "../../../../../utils/i18n/lang";
import { sequenceS } from "fp-ts/Apply";
import {
    LiveResultsClubs,
    LiveResultsIndividuals,
    LiveResultsProps,
} from "../../../../../components/org/event/LiveResults";
import { useEventGunShots } from "../../../../../hooks/timing";

type SsrError =
    | { type: "query"; reason: string }
    | {
          type: "fetch";
          reason: QueryError;
      };

export const getServerSideProps = async (
    context: GetServerSidePropsContext
) => {
    return pipe(
        context.query,
        liveResultsRoute.queryCodec.decode,
        either.mapLeft(
            (e): SsrError => ({
                type: "query",
                reason: errorsToMessage(e),
            })
        ),
        either.map((query) => ({ query })),
        taskEither.fromEither,
        taskEither.chain(({ query }) =>
            pipe(
                executeSupabaseQuery(
                    eventDetailQueryId(query.eventId),
                    eventDetailCodec,
                    eventDetailQuery(query.eventId),
                    supabase
                ),
                taskEither.mapLeft(
                    (e): SsrError => ({
                        type: "fetch",
                        reason: e,
                    })
                ),
                taskEither.map((event) => ({
                    query,
                    event,
                }))
            )
        ),
        task.map(
            either.foldW(
                (e) => {
                    reportError("live results page", e);
                    return {
                        notFound: true as const,
                    };
                },
                (props) => ({
                    props,
                })
            )
        ),
        runTask
    );
};

export default function LiveResultsPage(
    p: InferGetServerSidePropsType<typeof getServerSideProps>
) {
    return (
        <Container maxWidth={"xl"}>
            <Grid
                container
                direction={"column"}
                spacing={1}
                sx={{ height: "100vh" }}
            >
                <Grid item>
                    <EventTitle {...p.event} />
                </Grid>

                {pipe(
                    p.event.races,
                    nonEmptyArray.fromArray,
                    option.map(nonEmptyArray.sort(eventDetailRaceOrd)),
                    option.fold(
                        () => <p>No race TODO</p>,
                        (races) => (
                            <NonEmptyRaceList
                                races={races}
                                defaultSelectedRaceId={p.query.raceId}
                                eventId={p.query.eventId}
                            />
                        )
                    )
                )}
            </Grid>
        </Container>
    );
}

type Mode = "individuals" | "clubs";

interface NonEmptyRaceListProps {
    races: NonEmptyArray<EventDetailRace>;
    eventId: EventId;
    defaultSelectedRaceId?: RaceId;
}
const NonEmptyRaceList = (p: NonEmptyRaceListProps) => {
    const m = useTranslation(msg);
    const [categoryFilter, setCategoryFilter] = React.useState<Array<string>>(
        []
    );
    const [includeResultsWithoutBibPass, setIncludeResultsWithoutBibPass] =
        React.useState(false);

    const raceTabState = useRaceTabState(
        p.races,
        liveResultsRoute,
        p.defaultSelectedRaceId,
        () => {
            setCategoryFilter([]);
        }
    );

    const activeRace = pipe(
        p.races,
        array.findFirst((race) => race.id === raceTabState.selectedRaceId)
    );

    const gunShotsQuery = useEventGunShots(p.eventId);

    const liveResultsProps = pipe(
        activeRace,
        option.filterMap((race) =>
            pipe(
                race.gunshot,
                option.fromNullable,
                optionOrChain(() =>
                    pipe(
                        {
                            gunshotsData: queryStateData(gunShotsQuery.state),
                            startInterval: pipe(
                                race.start_time,
                                option.fromNullable,
                                option.map(
                                    validIsoDateStringFromDateCodec.encode
                                ),
                                option.map(
                                    (raceStart): Interval => ({
                                        start: addMinutes(raceStart, -5),
                                        end: addMinutes(raceStart, 5),
                                    })
                                )
                            ),
                        },
                        sequenceS(option.Apply),
                        option.chain((ctx) =>
                            pipe(
                                ctx.gunshotsData,
                                array.findFirst((row) =>
                                    pipe(
                                        row.gunshot,
                                        // (timeWithoutZone) =>
                                        //     `${timeWithoutZone}+01` as ValidIsoDateString, // TODO!!! hardcoded zone
                                        validIsoDateStringFromDateCodec.encode,
                                        (gunshotDate) =>
                                            isWithinInterval(
                                                gunshotDate,
                                                ctx.startInterval
                                            )
                                    )
                                )
                            )
                        )
                    )
                ),
                option.map(
                    ({ gunshot }): LiveResultsProps => ({
                        race,
                        gunshot,
                        eventId: p.eventId,
                        categoryFilter,
                    })
                )
            )
        )
    );

    const [mode, setMode] = React.useState<Mode>("clubs");

    return (
        <>
            <Grid item>
                <RaceTabs races={p.races} state={raceTabState} />
            </Grid>
            {pipe(
                activeRace,
                option.map(
                    flow(
                        (race) => race.race_category,
                        array.map((option) => option.category.title)
                    )
                ),
                option.chain(nonEmptyArray.fromArray),
                option.filter(() => option.isSome(liveResultsProps)),
                option.fold(constNull, (options) => (
                    <Grid item>
                        <Autocomplete
                            value={categoryFilter}
                            onChange={(_, newValue) => {
                                setCategoryFilter(newValue);
                            }}
                            multiple={true}
                            renderInput={(params) => (
                                <TextField {...params} label={"Kategorie"} />
                            )}
                            loading={option.isNone(activeRace)}
                            options={options}
                            filterSelectedOptions={true}
                        />
                    </Grid>
                ))
            )}

            {pipe(
                liveResultsProps,
                option.fold(
                    () => (
                        <Grid item>
                            <Typography>{m.notStarted}</Typography>
                        </Grid>
                    ),
                    (p) => {
                        return (
                            <>
                                <Grid item>
                                    {p.race.club_scoring == null ? null : (
                                        <Grid item>
                                            <ButtonGroup
                                                size={"small"}
                                                variant={"outlined"}
                                            >
                                                <Button
                                                    onClick={() => {
                                                        setMode("clubs");
                                                    }}
                                                    variant={
                                                        mode === "clubs"
                                                            ? "contained"
                                                            : undefined
                                                    }
                                                >
                                                    týmy
                                                </Button>
                                                <Button
                                                    onClick={() => {
                                                        setMode("individuals");
                                                    }}
                                                    variant={
                                                        mode === "individuals"
                                                            ? "contained"
                                                            : undefined
                                                    }
                                                >
                                                    jednotlivci
                                                </Button>
                                            </ButtonGroup>
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item flexGrow={1}>
                                    {p.race.club_scoring != null &&
                                    mode === "clubs" ? (
                                        <LiveResultsClubs
                                            {...p}
                                            clubScoring={p.race.club_scoring}
                                        />
                                    ) : (
                                        <LiveResultsIndividuals
                                            {...p}
                                            includeResultsWithoutBibPass={
                                                includeResultsWithoutBibPass
                                            }
                                            onIncludeResultsWithoutBibPassChange={
                                                setIncludeResultsWithoutBibPass
                                            }
                                        />
                                    )}
                                </Grid>
                            </>
                        );
                    }
                )
            )}
        </>
    );
};

const msg = messages(
    {
        notStarted: "Race has not yet begun",
    },
    {
        cs: {
            notStarted: "Nebylo odstartováno",
        },
    }
);
